import { API_ENDPOINTS } from '../constants/api';
import { AUTH_STORAGE } from '../constants/auth';

type ErrorLevel = 'error' | 'warn' | 'info';

interface ClientErrorPayload {
  level: ErrorLevel;
  message: string;
  stack?: string;
  route: string;
  method: string;
  user_id?: string;
  details?: unknown;
}

const MAX_TEXT_LENGTH = 12000;

function truncate(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  const text = String(value);
  return text.length > MAX_TEXT_LENGTH ? text.slice(0, MAX_TEXT_LENGTH) : text;
}

function currentUser(): string | undefined {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE.USER);
    if (!raw) return undefined;
    const user = JSON.parse(raw);
    return user?.username || String(user?.id || '');
  } catch {
    return undefined;
  }
}

function normalizeReason(reason: unknown): { message: string; stack?: string; details?: unknown } {
  if (reason instanceof Error) {
    return {
      message: reason.message || 'Unhandled error',
      stack: truncate(reason.stack),
    };
  }

  if (typeof reason === 'object' && reason !== null) {
    const maybeError = reason as { message?: unknown; stack?: unknown };
    return {
      message: truncate(maybeError.message) || 'Unhandled error',
      stack: truncate(maybeError.stack),
      details: reason,
    };
  }

  return { message: truncate(reason) || 'Unhandled error' };
}

function sendError(payload: ClientErrorPayload): void {
  const body = JSON.stringify({
    ...payload,
    message: truncate(payload.message) || 'Unhandled error',
    stack: truncate(payload.stack),
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(API_ENDPOINTS.ERRORS, blob);
    return;
  }

  fetch(API_ENDPOINTS.ERRORS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function logClientError(input: {
  level?: ErrorLevel;
  message: string;
  stack?: string;
  details?: unknown;
}): void {
  sendError({
    level: input.level || 'error',
    message: input.message,
    stack: input.stack,
    route: window.location.pathname + window.location.search,
    method: 'CLIENT',
    user_id: currentUser(),
    details: {
      ...((input.details && typeof input.details === 'object') ? input.details as Record<string, unknown> : { detail: input.details }),
      userAgent: navigator.userAgent,
    },
  });
}

export function registerGlobalErrorLogging(): void {
  if (process.env.NODE_ENV === 'test') return;

  window.addEventListener('error', event => {
    logClientError({
      message: event.message || 'Window error',
      stack: truncate(event.error?.stack),
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', event => {
    const normalized = normalizeReason(event.reason);
    logClientError({
      message: normalized.message,
      stack: normalized.stack,
      details: normalized.details,
    });
  });
}
